import React, { useEffect, useRef, useState } from "react";

import "./style.scss";

interface AudioAgentModalProps {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  content: string;
}

export const AudioAgentModal: React.FC<AudioAgentModalProps> = ({
  isOpen,
  onClose,
  title,
  content,
}) => {
  const [isPlaying, setIsPlaying] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const utteranceRef = useRef<SpeechSynthesisUtterance | null>(null);
  const isInitialMount = useRef(true);

  const handlePlay = React.useCallback(() => {
    if (!content) {
      console.warn("No content to play");
      return;
    }

    // Check if speech synthesis is supported
    if (typeof window === "undefined" || !window.speechSynthesis) {
      console.error("Speech synthesis is not supported in this browser");
      alert(
        "Text-to-speech is not supported in your browser. Please use Chrome, Edge, or Safari."
      );
      return;
    }

    // If paused, resume
    if (isPaused) {
      window.speechSynthesis.resume();
      setIsPlaying(true);
      setIsPaused(false);
      return;
    }

    // Create new utterance
    const utterance = new SpeechSynthesisUtterance(content);
    utterance.lang = "en-US";
    utterance.rate = 1.0;
    utterance.pitch = 1.0;
    utterance.volume = 1.0;

    // Handle events
    utterance.onstart = () => {
      setIsPlaying(true);
      setIsPaused(false);
    };

    utterance.onend = () => {
      setIsPlaying(false);
      setIsPaused(false);
      utteranceRef.current = null;
    };

    utterance.onerror = event => {
      console.error("Speech synthesis error:", event);
      setIsPlaying(false);
      setIsPaused(false);
      utteranceRef.current = null;
    };

    utteranceRef.current = utterance;
    window.speechSynthesis.speak(utterance);
  }, [content, isPaused]);

  // Initialize speech synthesis
  useEffect(() => {
    if (!isOpen) {
      // Reset state when modal closes
      setIsPlaying(false);
      setIsPaused(false);
      if (utteranceRef.current) {
        window.speechSynthesis.cancel();
        utteranceRef.current = null;
      }
      return;
    }

    // Auto-play when modal opens (only on first open, not on every render)
    if (isInitialMount.current && content) {
      isInitialMount.current = false;
      handlePlay();
    }

    return () => {
      if (utteranceRef.current) {
        window.speechSynthesis.cancel();
      }
    };
  }, [isOpen, content, handlePlay]);

  // Reset initial mount flag when modal closes
  useEffect(() => {
    if (!isOpen) {
      isInitialMount.current = true;
    }
  }, [isOpen]);

  const handlePause = () => {
    if (isPlaying && !isPaused) {
      window.speechSynthesis.pause();
      setIsPaused(true);
      setIsPlaying(false);
    } else if (isPaused) {
      window.speechSynthesis.resume();
      setIsPaused(false);
      setIsPlaying(true);
    }
  };

  const handleStop = () => {
    window.speechSynthesis.cancel();
    setIsPlaying(false);
    setIsPaused(false);
    utteranceRef.current = null;
  };

  // Handle Escape key
  useEffect(() => {
    if (!isOpen) return;

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        handleStop();
        onClose();
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  return (
    <div
      className="audio-agent-modal-overlay"
      onClick={onClose}
      style={{ display: isOpen ? "flex" : "none" }}
    >
      <div className="audio-agent-modal" onClick={e => e.stopPropagation()}>
        {/* Header */}
        <div className="audio-agent-modal__header">
          <div className="audio-agent-modal__header-left">
            <svg
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              style={{ marginRight: "8px" }}
            >
              <path d="M11 5 6 9H2v6h4l5 4V5Z" />
              <path d="M15.54 8.46a5 5 0 0 1 0 7.07" />
              <path d="M19.07 4.93a10 10 0 0 1 0 14.14" />
            </svg>
            <h3 className="audio-agent-modal__title">Izba Audio Agent</h3>
          </div>
          <button
            className="audio-agent-modal__close"
            onClick={onClose}
            title="Close"
          >
            ×
          </button>
        </div>

        {/* Content Card */}
        <div className="audio-agent-modal__content-card">
          <h4 className="audio-agent-modal__content-title">{title}</h4>
          <p className="audio-agent-modal__content-text">{content}</p>
        </div>

        {/* Controls */}
        <div className="audio-agent-modal__controls">
          <button
            className="audio-agent-modal__btn audio-agent-modal__btn--pause"
            onClick={handlePause}
            disabled={!isPlaying && !isPaused}
            title={isPaused ? "Resume" : "Pause"}
          >
            <svg
              width="20"
              height="20"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              {isPaused ? (
                <polygon points="5 3 19 12 5 21 5 3" />
              ) : (
                <>
                  <rect x="6" y="4" width="4" height="16" />
                  <rect x="14" y="4" width="4" height="16" />
                </>
              )}
            </svg>
            {isPaused ? "Resume" : "Pause"}
          </button>
          <button
            className="audio-agent-modal__btn audio-agent-modal__btn--stop"
            onClick={handleStop}
            disabled={!isPlaying && !isPaused}
            title="Stop"
          >
            <svg
              width="20"
              height="20"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <rect x="6" y="6" width="12" height="12" />
            </svg>
            Stop
          </button>
        </div>
      </div>
    </div>
  );
};
